import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useProfiles } from "@/lib/profiles";
import { LETTER_TEMPLATES, getTemplate, fmtLongDate } from "@/lib/letterTemplates";
import { printLetter } from "@/lib/printLetter";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { FileText, Printer, Copy, Trash2, Search } from "lucide-react";
import { toast } from "sonner";

const menuCls = "bg-[#121212] border-white/10 text-white rounded-none";

export default function Letters() {
  const navigate = useNavigate();
  const { profiles, activeProfileId, setActiveProfileId } = useProfiles();
  const profileId = activeProfileId || profiles[0]?.id;
  const profile = profiles.find((p) => p.id === profileId);

  const [letters, setLetters] = useState([]);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState("");
  const [busyId, setBusyId] = useState("");

  const load = useCallback(async () => {
    if (!profileId) return;
    setLoading(true);
    try {
      const { data } = await api.get(`/letters?profile_id=${profileId}`);
      setLetters(data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to load letters");
      setLetters([]);
    } finally {
      setLoading(false);
    }
  }, [profileId]);

  useEffect(() => { load(); }, [load]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return letters;
    return letters.filter((l) => `${l.title} ${l.ref_no} ${l.recipient_name} ${l.subject}`.toLowerCase().includes(q));
  }, [letters, query]);

  const duplicate = async (l) => {
    if (busyId) return;
    setBusyId(l.id);
    try {
      const { id, user_id, created_by_name, updated_by_name, created_at, updated_at, ref_no, ...rest } = l; // eslint-disable-line no-unused-vars
      const { data } = await api.post("/letters", { ...rest, title: `${l.title} (copy)`, ref_no: "" });
      toast.success("Letter duplicated");
      navigate(`/letters/${data.id}`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to duplicate");
    } finally { setBusyId(""); }
  };

  const remove = async (l) => {
    if (!window.confirm(`Delete "${l.title}"? This cannot be undone.`)) return;
    try {
      await api.delete(`/letters/${l.id}`);
      toast.success("Letter deleted");
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to delete");
    }
  };

  if (!profileId) {
    return <div className="px-4 sm:px-6 lg:px-10 py-10 text-neutral-500 font-mont text-xs uppercase tracking-[0.3em]">No business profile available yet.</div>;
  }

  return (
    <div className="px-4 sm:px-6 lg:px-10 py-6 lg:py-10">
      <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4 mb-10">
        <div>
          <p className="font-mont text-[10px] uppercase tracking-[0.3em] text-[#D4AF37] mb-3">— Letters</p>
          <h1 className="font-serif text-3xl sm:text-4xl lg:text-5xl tracking-tight text-white">Letters &amp; Documents</h1>
          <p className="text-neutral-500 text-sm mt-3">Create appointment letters, certificates and more on your company letterhead.</p>
        </div>
        {profiles.length > 1 && (
          <Select value={profileId} onValueChange={setActiveProfileId}>
            <SelectTrigger className="bg-transparent border border-white/20 rounded-none text-white font-mont w-full sm:w-64"><SelectValue /></SelectTrigger>
            <SelectContent className={menuCls}>
              {profiles.map((p) => (<SelectItem key={p.id} value={p.id} className="font-mont text-sm">{p.name}</SelectItem>))}
            </SelectContent>
          </Select>
        )}
      </div>

      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-4">
        <div className="font-mont text-[10px] uppercase tracking-[0.3em] text-neutral-500">— Saved letters ({letters.length})</div>
        <div className="relative w-full sm:w-72">
          <Search className="w-4 h-4 text-neutral-600 absolute left-3 top-1/2 -translate-y-1/2" strokeWidth={1.5} />
          <Input value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Search letters…" className="pl-9 bg-transparent border border-white/20 rounded-none text-white font-mont h-9" />
        </div>
      </div>

      <div className="border border-white/10 divide-y divide-white/10 mb-12">
        {loading ? (
          <div className="py-16 text-center font-mont text-xs uppercase tracking-[0.3em] text-neutral-600">Loading…</div>
        ) : filtered.length === 0 ? (
          <div className="py-16 text-center font-mont text-xs uppercase tracking-[0.3em] text-neutral-600">
            {letters.length === 0 ? "No letters yet — pick a template below" : "No letters match your search"}
          </div>
        ) : (
          filtered.map((l) => (
            <div key={l.id} data-testid={`letter-row-${l.id}`} className="flex items-center justify-between gap-3 px-4 py-4 hover:bg-white/[0.02]">
              <button onClick={() => navigate(`/letters/${l.id}`)} className="flex-1 min-w-0 text-left">
                <div className="text-white text-sm truncate">{l.title || getTemplate(l.template).label}</div>
                <div className="font-mont text-[10px] text-neutral-500 truncate mt-0.5">
                  {l.ref_no}{l.date && ` · ${fmtLongDate(l.date)}`}{l.created_by_name && ` · by ${l.created_by_name}`}
                </div>
              </button>
              <div className="flex items-center gap-3 flex-shrink-0">
                <button title="Print / save as PDF" onClick={() => printLetter(l, profile)} className="text-neutral-500 hover:text-white"><Printer className="w-4 h-4" strokeWidth={1.5} /></button>
                <button title="Duplicate" disabled={!!busyId} onClick={() => duplicate(l)} className="text-neutral-500 hover:text-white disabled:opacity-40"><Copy className="w-4 h-4" strokeWidth={1.5} /></button>
                <button title="Delete" onClick={() => remove(l)} className="text-neutral-500 hover:text-red-400"><Trash2 className="w-4 h-4" strokeWidth={1.5} /></button>
              </div>
            </div>
          ))
        )}
      </div>

      <div className="font-mont text-[10px] uppercase tracking-[0.3em] text-neutral-500 mb-4">— Create new</div>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-px bg-white/10 border border-white/10 mb-4">
        {LETTER_TEMPLATES.map((t) => (
          <button
            key={t.key}
            data-testid={`new-letter-${t.key}`}
            onClick={() => navigate(`/letters/new?template=${t.key}`)}
            className="bg-[#121212] hover:bg-[#181818] text-left p-6 transition-colors group"
          >
            <FileText className="w-5 h-5 text-[#D4AF37] mb-4" strokeWidth={1.5} />
            <div className="font-serif text-lg text-white group-hover:text-[#D4AF37] transition-colors">{t.label}</div>
            <div className="font-mont text-[11px] text-neutral-500 mt-2 leading-relaxed">{t.description}</div>
          </button>
        ))}
      </div>
    </div>
  );
}
